/**
 * Queue message payloads (docs/55 §2.1, docs/53 §1.1, §7). Every consumer parses its batch with these schemas
 * and acks nothing it couldn't parse (those go to the dead-letter queue). Messages carry ids, not contents,
 * with one exception: `inbound.lead` carries the form fields once, from `sites` (which has no D1) to `jobs`
 * (which encrypts them on insert).
 */
import { z } from 'zod';
import { Actor } from './actor.ts';
import { Email, IsoDateTime, PhoneInput } from './common.ts';
import { InboundKind, MediaSource, Channel } from './enums.ts';
import { EventType } from './events.ts';
import { LeadOrigin } from './measure.ts';
import {
  DeliveryId,
  EventId,
  JobId,
  LocationId,
  MediaAssetId,
  OutputId,
  PodId,
  ProposalId,
  QueueMessageId,
  ReviewRequestId,
  SiteId,
  TenantId,
  UploadId,
  UserId,
} from './ids.ts';

/** Logical queue names. Wrangler maps them to `ff-<name>-<env>` resources (config/wrangler). */
export const QUEUES = {
  inbound: 'inbound',
  notify: 'notify',
  messages: 'messages',
  media: 'media',
  gbp: 'gbp',
  publish: 'publish',
} as const;
export type QueueName = keyof typeof QUEUES;

/** R2 `intake` keys always start with the tenant prefix (docs/53 §5.2). */
export const IntakeKey = z
  .string()
  .regex(/^t\/tnt_[a-z0-9]+\/intake\/[A-Za-z0-9._-]+$/)
  .meta({ examples: ['t/tnt_riveraplumb01/intake/upl_abcdefghijklmnopqrstuvwxyz'] });

const base = <K extends string>(kind: K) => ({ kind: z.literal(kind), v: z.literal(1), id: QueueMessageId, enqueuedAt: IsoDateTime });

export const InboundLead = z.object({
  ...base('inbound.lead'),
  host: z.string().max(253),
  tenantId: TenantId,
  siteId: SiteId,
  version: z.number().int().min(1).nullable(),
  receivedAt: IsoDateTime,
  fields: z.object({
    name: z.string().max(120),
    phone: PhoneInput.nullable(),
    email: Email.nullable(),
    message: z.string().max(5000).nullable(),
    service: z.string().max(64).nullable(),
    town: z.string().max(80).nullable(),
    pagePath: z.string().max(512),
  }),
  smsConsent: z.boolean(),
  smsConsentTextVersion: z.string().max(40).nullable(),
  signals: z.object({ /** Null when the page couldn't measure it (the form posted with JavaScript off). */ fillMs: z.number().int().min(0).nullable(), sameOrigin: z.boolean(), honeypot: z.boolean(), uaFamily: z.string().max(40).nullable() }),
  ipHash: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
  /**
   * Where the enquiry came from (R6-18): the visit's first page, the site that sent it, UTM tags and gclid, captured
   * first party by the site script. Null when the page couldn't say (JavaScript off). Optional: messages queued before
   * it existed still parse.
   */
  origin: LeadOrigin.nullable().optional(),
  /** The daily synthetic lead (sites verified its signed header): counted as a delivery check, never stored or sent on. */
  synthetic: z.literal(true).optional(),
});

export const InboundEmail = z.object({
  ...base('inbound.email'),
  tenantId: TenantId,
  purpose: InboundKind,
  /** R2 `intake` key of the raw message: t/<tenant>/intake/<id>.eml */
  r2Key: IntakeKey,
  senderUserId: UserId.nullable(),
  replyToken: z.string().max(128).nullable(),
  attachments: z.number().int().min(0).max(50),
});

export const InboundMessage = z.discriminatedUnion('kind', [InboundLead, InboundEmail]);

export const NotifyEvent = z.object({
  ...base('notify.event'),
  eventId: EventId,
  eventType: EventType,
  tenantId: TenantId,
  locationId: LocationId.nullable(),
  subject: z.object({ type: z.string().max(40), id: z.string().max(64) }),
  /** Restrict to these users; omitted means everyone the routing rules pick. */
  audience: z.array(UserId).max(100).optional(),
  dedupeKey: z.string().max(128),
});

export const MessagesSend = z.object({
  ...base('messages.send'),
  tenantId: TenantId,
  template: z.enum(['review_request', 'review_reminder', 'opt_out_confirmation']),
  reviewRequestId: ReviewRequestId.nullable(),
  channel: z.enum(['sms', 'email']),
  scheduledFor: IsoDateTime,
  attempt: z.number().int().min(1).max(10),
  dedupeKey: z.string().max(128),
});

export const MediaProcess = z.object({
  ...base('media.process'),
  tenantId: TenantId,
  uploadId: UploadId,
  r2Key: IntakeKey,
  contentType: z.string().max(100),
  bytes: z.number().int().positive(),
  source: MediaSource,
  createdBy: UserId.nullable(),
  jobId: JobId.nullable(),
  locationId: LocationId.nullable(),
  /** The library row the app made when the upload finished (k11). Absent on messages from before the pipeline. */
  assetId: MediaAssetId.nullable().optional(),
});

export const GbpWrite = z.object({
  ...base('gbp.write'),
  tenantId: TenantId,
  locationId: LocationId,
  podId: PodId,
  op: z.enum(['reply', 'post', 'photo', 'field', 'hours']),
  subject: z.object({ type: z.enum(['output', 'proposal', 'decision']), id: z.union([OutputId, ProposalId, z.string().max(64)]) }),
  idempotencyKey: z.string().max(128),
});

export const GbpSync = z.object({
  ...base('gbp.sync'),
  tenantId: TenantId,
  locationId: LocationId,
  scope: z.enum(['reviews', 'profile', 'metrics', 'updates']),
  reason: z.enum(['daily', 'pubsub', 'manual']),
});

export const GbpMessage = z.discriminatedUnion('kind', [GbpWrite, GbpSync]);

/** `media`: a photo was approved, placed, refocused or removed (k11). */
export const PublishReason = z.enum(['go_live', 'proposal', 'rollback', 'rollout', 'notice', 'hours', 'project', 'manual', 'retry', 'media']);
export type PublishReason = z.infer<typeof PublishReason>;

export const PublishRequest = z.object({
  ...base('publish.request'),
  tenantId: TenantId,
  siteId: SiteId,
  reason: PublishReason,
  requestedBy: Actor,
  proposalId: ProposalId.nullable(),
  dedupeKey: z.string().max(128),
});

/** One schema per queue: what a consumer must accept. */
export const QUEUE_SCHEMAS = {
  inbound: InboundMessage,
  notify: NotifyEvent,
  messages: MessagesSend,
  media: MediaProcess,
  gbp: GbpMessage,
  publish: PublishRequest,
} as const satisfies Record<QueueName, z.ZodType>;

export type QueueMessage<Q extends QueueName> = z.infer<(typeof QUEUE_SCHEMAS)[Q]>;

/** Delivery status updates flow back through webhooks, not queues; this is the shape the notify consumer writes. */
export const DeliveryWrite = z.object({ deliveryId: DeliveryId, channel: Channel, status: z.enum(['queued', 'sent', 'delivered', 'opened', 'clicked', 'failed', 'suppressed']) });
